import React, { FC, useMemo } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { SpinningLoader, Text } from '@weave/design-system';
import { useSelector } from 'react-redux';
import { LocationModel, selectCurrentLocationParentId } from 'redux/actions/location';
import { useGetChildrenLocationsQuery } from 'components/onboarding/handoff-hub/handoff-hub.queries';
import {
  UnifyLocationDetails,
  UnifyLocationRow,
} from './components/unify-locations-table/unify-locations-table.types';
import {
  UnifyHandoffProvider,
  type UnifyWizardStep,
  useUnifyHandoffContext,
} from './unify-handoff-context';
import { MetricResult } from '../handoff-metrics/handoff-metrics.types';
import { UnifySelectLocations } from './components/select-locations-step/unify-select-locations';
import { UnifyEvaluation } from './components/evaluation-step/unify-evaluation';
import { UnifyHandoffSnapshot } from './components/snapshot-step/unify-handoff-snapshot';

export interface UnifyHandoffProps {
  onCancel?: () => void;
  onSaveAndClose?: (locationDetailsMap?: Record<string, UnifyLocationDetails>) => void;
  onSubmit?: (resultsByLocation: Record<string, MetricResult[]>) => void;
  isSaving?: boolean;
  isSubmitting?: boolean;
  locationsList?: LocationModel[];
  handoffGroupId?: string;
  mode?: 'edit' | 'snapshot';
  initialStep?: UnifyWizardStep;
  initialLocationDetailsMap?: Record<string, UnifyLocationDetails>;
  initialSelectedLocationIds?: string[];
  maxSelections?: number;
  minSelections?: number;
}

const UnifyHandoffContent: FC = () => {
  const { currentStep, locations, isLoadingDraft } = useUnifyHandoffContext();

  if (isLoadingDraft) {
    return (
      <div css={styles.loadingContainer}>
        <SpinningLoader size="large" />
        <Text color="light" size="medium">
          Loading handoff draft details...
        </Text>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div css={styles.emptyContainer}>
        <Text color="light" size="medium">
          No locations found under this organization.
        </Text>
      </div>
    );
  }

  return (
    <div css={styles.wrapper}>
      {currentStep === 'snapshot' && <UnifyHandoffSnapshot />}
      {currentStep === 'select-locations' && <UnifySelectLocations />}
      {currentStep === 'evaluation' && <UnifyEvaluation />}
    </div>
  );
};

export const UnifyHandoff: React.FC<UnifyHandoffProps> = ({
  locationsList,
  handoffGroupId,
  mode = 'edit',
  initialStep: propInitialStep,
  initialLocationDetailsMap = {},
  initialSelectedLocationIds = [],
  maxSelections = 5,
  minSelections = 1,
  onCancel,
  onSaveAndClose = () => {},
  onSubmit = () => {},
  isSaving = false,
  isSubmitting = false,
}) => {
  const parentLocationId = useSelector(selectCurrentLocationParentId);
  const shouldFetchChildren = !locationsList || locationsList.length === 0;

  const { data: fetchedLocations = [], isLoading: isLoadingLocations } =
    useGetChildrenLocationsQuery(parentLocationId, {
      enabled: shouldFetchChildren && !!parentLocationId,
    });

  // Map LocationModels to standard UnifyLocationRow format
  const locationRows = useMemo<UnifyLocationRow[]>(() => {
    const list =
      locationsList && locationsList.length > 0 ? locationsList : fetchedLocations;
    if (!list) return [];
    return list
      .map((loc) => ({
        locationId: loc.LocationID || '',
        locationName: loc.Name || 'Unknown Location',
        slug: loc.Slug || '',
        verticalId: loc.VerticalID,
      }))
      .sort((a, b) => a.locationName.localeCompare(b.locationName));
  }, [locationsList, fetchedLocations]);

  // Determine initial step
  const initialStep: UnifyWizardStep = useMemo(() => {
    if (propInitialStep) return propInitialStep;
    if (mode === 'snapshot') return 'snapshot';
    return handoffGroupId ? 'evaluation' : 'select-locations';
  }, [propInitialStep, mode, handoffGroupId]);

  if (isLoadingLocations && locationRows.length === 0) {
    return (
      <div css={styles.loadingContainer}>
        <SpinningLoader size="large" />
        <Text color="light" size="medium">
          Loading locations...
        </Text>
      </div>
    );
  }

  return (
    <UnifyHandoffProvider
      locations={locationRows}
      initialStep={initialStep}
      initialSelectedLocationIds={initialSelectedLocationIds}
      initialLocationDetailsMap={initialLocationDetailsMap}
      initialGroupId={handoffGroupId}
      maxSelections={maxSelections}
      minSelections={minSelections}
      isSaving={isSaving}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSaveAndClose={onSaveAndClose}
      onSubmit={onSubmit}
    >
      <UnifyHandoffContent />
    </UnifyHandoffProvider>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  loadingContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${theme.spacing(8, 0)};
    gap: ${theme.spacing(2)};
  `,
  emptyContainer: css`
    padding: ${theme.spacing(4, 0)};
    text-align: center;
  `,
};
